import { baseSingleCampaignFormAdaptor } from './list.adaptor';

// Groups variant-level server product entries back into product-level objects
// that ResourcePickerField / ResourceListWithDelete can display.
function bogoProductsFromServer(serverProducts) {
  if (!Array.isArray(serverProducts) || serverProducts.length === 0) return [];
  const byProduct = new Map();
  for (const p of serverProducts) {
    const pid = p.productId?.toString();
    if (!pid) continue;
    if (!byProduct.has(pid)) {
      byProduct.set(pid, {
        productId: pid,
        isExclude: Boolean(p.isExcluded),
        isSelected: true,
        title: p.meta?.title ?? '',
        handle: p.meta?.handle ?? '',
        image: { src: p.meta?.images?.[0]?.originalSrc ?? null },
        variantIdList: [],
        variants: [],
        product: {
          productId: pid,
          title: p.meta?.title ?? '',
          handle: p.meta?.handle ?? '',
          image: p.meta?.images?.[0]?.originalSrc ?? null,
        },
      });
    }
    const entry = byProduct.get(pid);
    const vid = p.variantId?.toString();
    if (vid && !entry.variantIdList.includes(vid)) {
      entry.variantIdList.push(vid);
      const variantTitle =
        p.meta?.variants?.find(
          (v) => v.id?.toString() === vid || v.id?.toString().endsWith('/' + vid)
        )?.title ?? vid;
      entry.variants.push({ id: vid, title: variantTitle, productId: pid });
    }
  }
  return Array.from(byProduct.values());
}

function bogoCollectionsFromServer(serverCollections) {
  if (!Array.isArray(serverCollections) || serverCollections.length === 0) return [];
  return serverCollections.map((c) => ({
    collectionId: c.collectionId?.toString(),
    isExclude: Boolean(c.isExcluded),
    isSelected: true,
    title: c.meta?.title ?? '',
    collection: { title: c.meta?.title ?? '' },
  }));
}

// Transforms the flat server bogoConfig into the nested shape CampaignForm expects.
function bogoConfigAdaptor(cfg) {
  if (!cfg) return null;
  return {
    offerType: cfg.offerType,
    customerBuys: {
      minimumQuantity: cfg.minimumQuantity ?? 2,
      appliesToType: cfg.appliesToType ?? 'ALL_PRODUCTS',
      appliesToSelection: {
        productIds: cfg.productIds ?? [],
        collectionIds: cfg.collectionIds ?? [],
        tags: cfg.tags ?? [],
        vendors: cfg.vendors ?? [],
      },
      auctionProducts: bogoProductsFromServer(cfg.buyProducts),
      auctionCollections: bogoCollectionsFromServer(cfg.buyCollections),
      itemCountMode: cfg.itemCountMode === 'ACROSS_ALL_ITEMS' ? 'ANY_ELIGIBLE_ITEMS' : (cfg.itemCountMode ?? 'SAME_ITEM_ONLY'),
      minimumSpend: cfg.minimumSpend ?? '',
      exclusionsEnabled: cfg.exclusionsEnabled ?? false,
      activeExclusionType: cfg.activeExclusionType ?? null,
      exclusions: cfg.exclusions
        ? {
            ...cfg.exclusions,
            excludeAuctionProducts: bogoProductsFromServer(cfg.exclusions.excludeProducts),
            excludeAuctionCollections: bogoCollectionsFromServer(cfg.exclusions.excludeCollections),
          }
        : null,
    },
    customerGets: {
      rewardType: cfg.rewardType,
      rewardAppliesTo: cfg.rewardAppliesTo,
      rewardValue: {
        percentage: cfg.rewardPercentage ?? '',
        fixedAmount: cfg.rewardFixedAmount ?? '',
      },
      rewardSelection: {
        productIds: cfg.rewardProductIds ?? [],
        collectionIds: cfg.rewardCollectionIds ?? [],
      },
      rewardProducts: bogoProductsFromServer(cfg.rewardProducts),
      rewardCollections: bogoCollectionsFromServer(cfg.rewardCollections),
      discountedItemQuantity: cfg.discountedItemQuantity ?? 1,
      applicationMode: cfg.applicationMode ?? 'ONCE_PER_ORDER',
      maximumRewardsPerOrder: cfg.maximumRewardsPerOrder ?? '',
    },
    combination: {
      combineWithOtherDiscounts: cfg.combineWithOtherDiscounts ?? false,
      executionOrder: cfg.executionOrder ?? null,
    },
    storefrontDisplay: {
      storefrontDisplayType: cfg.storefrontDisplayType ?? null,
    },
    bogoNote: cfg.bogoNote ?? '',
  };
}

// ── Product Bundle ──────────────────────────────────────────────────────────
// Server slot `products` rows are one row per variant ({ productId, variantId,
// title, variantTitle, image, price, handle } — or the BOGO-style meta shape).
// Regroup them into the product-level rows ResourcePickerField displays, the
// same way bogoProductsFromServer does for buyProducts.
function bundleProductsFromServer(serverProducts) {
  if (!Array.isArray(serverProducts) || serverProducts.length === 0) return [];
  const byProduct = new Map();
  for (const p of serverProducts) {
    const pid = p?.productId?.toString();
    if (!pid) continue;
    const title = p.title ?? p.meta?.title ?? '';
    const handle = p.handle ?? p.meta?.handle ?? '';
    const image = p.image ?? p.meta?.image ?? p.meta?.images?.[0]?.originalSrc ?? null;
    if (!byProduct.has(pid)) {
      byProduct.set(pid, {
        productId: pid,
        isExclude: false,
        isSelected: true,
        title,
        handle,
        image: { src: image },
        variantIdList: [],
        variants: [],
        product: { productId: pid, title, handle, image },
      });
    }
    const entry = byProduct.get(pid);
    const vid = p?.variantId?.toString();
    if (vid && !entry.variantIdList.includes(vid)) {
      entry.variantIdList.push(vid);
      entry.variants.push({
        id: vid,
        title: p.variantTitle ?? p.meta?.variants?.[0]?.title ?? '',
        productId: pid,
        price: p.price ?? p.meta?.price ?? p.meta?.variants?.[0]?.price ?? undefined,
      });
    }
  }
  return Array.from(byProduct.values());
}

// Display rows from the picker meta the backend stores (titles, images, picked
// variants); bare ids are only the fallback for campaigns saved before that meta.
function productRowsFromMeta(products, ids) {
  return Array.isArray(products) && products.length ? bundleProductsFromServer(products) : idsToProductRows(ids);
}

function bundleSlotFromServer(slot, index, excludedTitles) {
  const type = slot?.selectionType || 'specific_product';
  const collections = Array.isArray(slot?.collections) ? slot.collections : [];
  const collectionIds = Array.isArray(slot?.collectionIds) ? slot.collectionIds : [];
  return {
    key: `slot-loaded-${slot?.position ?? index}`,
    title: slot?.title ?? '',
    selectionType: type,
    auctionProducts: type === 'specific_product' ? bundleProductsFromServer(slot?.products) : [],
    auctionCollections:
      type === 'collection'
        ? (collections.length
            ? collections.map((c) => ({
                collectionId: c.collectionId?.toString(),
                isExclude: false,
                isSelected: true,
                title: c.title ?? '',
                image: c.image ?? null,
                collection: { title: c.title ?? '' },
              }))
            : collectionIds.map((id) => ({
                collectionId: id?.toString(),
                isExclude: false,
                isSelected: true,
                title: '',
                collection: { title: '' },
              })))
        : [],
    auctionTags: type === 'tag' ? (slot?.tags ?? []).map((tagName) => ({ tagName, isExclude: false })) : [],
    auctionVendors:
      type === 'vendor' ? (slot?.vendors ?? []).map((vendorId) => ({ vendorId, isExclude: false })) : [],
    auctionProductTypes:
      type === 'product_type'
        ? (slot?.productTypes ?? []).map((productTypeName) => ({ productTypeName, isExclude: false }))
        : [],
    requiredQty: slot?.requiredQty ?? 1,
    maxQty: slot?.maxQty ?? '',
    isOptional: Boolean(slot?.isOptional),
    // Slots store bare ids; the server hands back a titles map (bundleConfig.excludedProductTitles).
    // Anything it could not resolve stays '' and the slot row fills it from the coverage preview
    // or falls back to a readable label.
    excludeProducts: (slot?.excludeProductIds ?? []).map((id) => ({
      productId: id?.toString(),
      title: excludedTitles?.[id?.toString()] ?? '',
    })),
    variantOptionValues: Array.isArray(slot?.variantOptionValues) ? slot.variantOptionValues : [],
  };
}

// F4.1 — fills in any field the server's BundleComboDto omitted (e.g. a
// campaign saved before Combo existed) with the same defaults the builder
// uses for a brand-new bundle, so BundleComboCard never has to guard against
// missing keys itself. productId round-trips untouched — see the comment on
// bundleComboToDto in save.adaptor.js (rule 2, ownership is permanent).
function bundleComboFromServer(combo, flat) {
  return {
    enabled: !!combo?.enabled,
    title: combo?.title ?? '',
    priceMode: combo?.priceMode === 'computed' ? 'computed' : 'fixed',
    price: combo?.price ?? '',
    priceSync: !!combo?.priceSync,
    consolidatedOptions: !!combo?.consolidatedOptions,
    showContents: combo?.showContents !== false,
    productId: combo?.productId ?? null,
    // Read-only lifecycle fields (spec D4/D5), written by the backend only and
    // never sent back by bundleComboToDto. The backend (comboStatusSummary in
    // bundle-combo.utils.ts) puts them FLAT on the campaign payload:
    // comboStatus (ineligible | publishing | published | failed | paused),
    // comboReasons, comboReasonCount, comboFailedReason, comboProductUrl,
    // comboNotice ({ kind: 'drift_draft', message, reasonCodes, read }).
    // They are also read from the stored combo JSON (statusReasons, ...) as a
    // fallback.
    status: flat?.comboStatus ?? combo?.status ?? null,
    reasons: Array.isArray(flat?.comboReasons)
      ? flat.comboReasons
      : Array.isArray(combo?.statusReasons) ? combo.statusReasons : [],
    reasonCount: Number.isFinite(Number(flat?.comboReasonCount ?? combo?.statusReasonCount))
      ? Number(flat?.comboReasonCount ?? combo?.statusReasonCount)
      : null,
    error: flat?.comboFailedReason ?? combo?.failedReason ?? null,
    productUrl: flat?.comboProductUrl ?? combo?.productAdminUrl ?? null,
    productHandle: combo?.productHandle ?? null,
    notice: flat?.comboNotice ?? combo?.notice ?? null,
    // Drift (D5): the product was set to Draft with an unread drift_draft notice.
    drift: (flat?.comboNotice ?? combo?.notice)?.kind === 'drift_draft' && !(flat?.comboNotice ?? combo?.notice)?.read,
  };
}

// Transforms the server bundleConfig into the shape the bundle builder holds.
function bundleConfigAdaptor(cfg, flat) {
  if (!cfg) return null;
  const slots = [...(cfg.slots ?? [])]
    .sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0))
    .map((slot, index) => bundleSlotFromServer(slot, index, cfg.excludedProductTitles));
  const freeGifts = bundleProductsFromServer(
    (cfg.reward?.freeGifts ?? []).map((g) => ({
      productId: g.productId,
      variantId: g.variantId,
      title: g.meta?.title ?? g.title ?? '',
      image: g.meta?.image ?? g.image ?? null,
      price: g.meta?.price ?? g.price,
      handle: g.meta?.handle ?? g.handle ?? '',
    }))
  );
  const setTiers = Array.isArray(cfg.setTiers) ? cfg.setTiers : [];
  return {
    title: cfg.title ?? '',
    note: cfg.note ?? '',
    slots,
    reward: {
      type: cfg.reward?.type ?? 'percentage',
      value: cfg.reward?.value ?? 0,
      freeGifts,
    },
    applicationMode: cfg.applicationMode ?? 'ONCE_PER_ORDER',
    rewardAllocation: cfg.rewardAllocation === 'most_expensive' ? 'most_expensive' : 'cheapest',
    maxSetsPerOrder: cfg.maxSetsPerOrder ?? 5,
    tiersEnabled: setTiers.length > 0,
    setTiers: setTiers.length > 0 ? setTiers : [{ minSets: 3, value: 30 }],
    combineMode: cfg.combineMode ?? 'best_wins',
    executionOrder: cfg.executionOrder ?? 'AFTER_OTHER_DISCOUNTS',
    storefrontDisplay: cfg.storefrontDisplay ?? 'BOTH',
    storefrontLayout: cfg.storefrontLayout ?? 'AUTO',
    combo: bundleComboFromServer(cfg.combo, flat),
  };
}

// ── Cross-sell ──────────────────────────────────────────────────────────────
// The xsellConfig contract (plan §4.2) sends bare ids (trigger.ids,
// offer.productIds/poolIds/exclusions) with no per-product display meta —
// unlike a bundle slot's `products` rows, there's no title/image/handle to
// regroup here. Build picker-shaped rows with just the id so
// ResourcePickerField can still render them as "selected"; the row fills in
// its title once the merchant interacts with the picker again or the live
// offer-preview response comes back (useXsellOfferPreview).
function idsToProductRows(ids) {
  return (Array.isArray(ids) ? ids : []).filter(Boolean).map((id) => {
    const pid = String(id);
    return {
      productId: pid,
      isExclude: false,
      isSelected: true,
      title: '',
      handle: '',
      image: { src: null },
      variantIdList: [],
      variants: [],
      product: { productId: pid, title: '', handle: '', image: null },
    };
  });
}

function idsToRuleRows(type, ids) {
  const list = Array.isArray(ids) ? ids : [];
  switch (type) {
    case 'collection':
      return list.map((id) => ({
        collectionId: String(id),
        isExclude: false,
        isSelected: true,
        title: '',
        collection: { title: '' },
      }));
    case 'tag':
      return list.map((tagName) => ({ tagName, isExclude: false }));
    case 'vendor':
      return list.map((vendorId) => ({ vendorId, isExclude: false }));
    case 'product_type':
      return list.map((productTypeName) => ({ productTypeName, isExclude: false }));
    default:
      return [];
  }
}

function xsellTriggerFromServer(trigger) {
  const type = trigger?.type || 'any';
  const ids = Array.isArray(trigger?.ids) ? trigger.ids : [];
  return {
    type,
    auctionProducts: type === 'specific_product' ? productRowsFromMeta(trigger?.products, ids) : [],
    auctionCollections: type === 'collection' ? idsToRuleRows('collection', ids) : [],
    auctionTags: type === 'tag' ? idsToRuleRows('tag', ids) : [],
    auctionVendors: type === 'vendor' ? idsToRuleRows('vendor', ids) : [],
    auctionProductTypes: type === 'product_type' ? idsToRuleRows('product_type', ids) : [],
    minQty: trigger?.minQty ?? 1,
    minSpend: trigger?.minSpend ?? '',
  };
}

function xsellOfferFromServer(offer) {
  const source = offer?.source || 'manual';
  const poolType = offer?.poolType || null;
  const poolIds = Array.isArray(offer?.poolIds) ? offer.poolIds : [];
  return {
    source,
    auctionProducts: source === 'manual' ? productRowsFromMeta(offer?.products, offer?.productIds) : [],
    poolType,
    auctionCollections: poolType === 'collection' ? idsToRuleRows('collection', poolIds) : [],
    auctionTags: poolType === 'tag' ? idsToRuleRows('tag', poolIds) : [],
    auctionVendors: poolType === 'vendor' ? idsToRuleRows('vendor', poolIds) : [],
    auctionProductTypes: poolType === 'product_type' ? idsToRuleRows('product_type', poolIds) : [],
    // Titles come from the server's read model (offer.excludedProductTitles); '' while unresolved.
    excludeProducts: (offer?.exclusions ?? []).map((id) => ({
      productId: id?.toString(),
      title: offer?.excludedProductTitles?.[id?.toString()] ?? '',
    })),
    pinnedProductIds: Array.isArray(offer?.pinnedProductIds) ? offer.pinnedProductIds : [],
    approvedProductIds: Array.isArray(offer?.approvedProductIds) ? offer.approvedProductIds : [],
    displayCount: offer?.displayCount ?? 3,
  };
}

// Transforms the server xsellConfig into the shape the cross-sell builder holds.
function xsellConfigAdaptor(cfg) {
  if (!cfg) return null;
  const reward = cfg.reward || {};
  const rewardType = reward.type || 'none';
  return {
    title: cfg.title ?? '',
    trigger: xsellTriggerFromServer(cfg.trigger),
    offer: xsellOfferFromServer(cfg.offer),
    reward: {
      type: rewardType,
      value: reward.value ?? 0,
      maxQty: reward.maxQty ?? 1,
      applyMode: reward.applyMode ?? 'auto',
    },
    combineMode: cfg.combineMode ?? 'best_wins',
    placement: cfg.placement ?? 'BOTH',
  };
}

// ── Upsell (Upgrade offer) ──────────────────────────────────────────────────
// The upsellConfig contract (upsell plan §2.1) sends bare ids
// (trigger.productIds, offer.productIds/poolIds) with no per-product display
// meta, same as xsell — reuse idsToProductRows/idsToRuleRows. The variant
// ladder ([{productId, option}]) round-trips as-is; the builder's
// ladder-axes analysis fills in titles/steps live.
function upsellTriggerFromServer(trigger) {
  return {
    auctionProducts: productRowsFromMeta(trigger?.products, trigger?.productIds),
  };
}

function upsellOfferFromServer(offer) {
  const source = ['manual', 'pool', 'smart'].includes(offer?.source) ? offer.source : 'manual';
  const poolType = offer?.poolType || null;
  const poolIds = Array.isArray(offer?.poolIds) ? offer.poolIds : [];
  return {
    source,
    auctionProducts: source === 'manual' ? productRowsFromMeta(offer?.products, offer?.productIds) : [],
    poolType,
    auctionCollections: poolType === 'collection' ? idsToRuleRows('collection', poolIds) : [],
    auctionTags: poolType === 'tag' ? idsToRuleRows('tag', poolIds) : [],
    auctionVendors: poolType === 'vendor' ? idsToRuleRows('vendor', poolIds) : [],
    auctionProductTypes: poolType === 'product_type' ? idsToRuleRows('product_type', poolIds) : [],
  };
}

// Transforms the server upsellConfig into the shape the upsell builder holds.
function upsellConfigAdaptor(cfg) {
  if (!cfg) return null;
  const incentive = cfg.incentive || {};
  return {
    mode: cfg.mode === 'variant_upgrade' ? 'variant_upgrade' : 'product_upgrade',
    trigger: upsellTriggerFromServer(cfg.trigger),
    ladder: (Array.isArray(cfg.ladder) ? cfg.ladder : [])
      .filter((e) => e && e.productId != null && e.option)
      .map((e) => ({ productId: String(e.productId), option: String(e.option) })),
    offer: upsellOfferFromServer(cfg.offer),
    incentive: {
      type: incentive.type || 'none',
      value: incentive.value ?? '',
    },
    combineMode: cfg.combineMode ?? 'best_wins',
    phrasing: cfg.phrasing ?? '',
    placement: cfg.placement ?? 'BOTH',
  };
}

// ── Free Gift ──────────────────────────────────────────────────────────────
// The giftConfig contract (CONTRACT text given with the gift-engine task)
// sends bare ids for scope (scope.ids), same shape as xsell's trigger — reuse
// idsToProductRows/idsToRuleRows above. Each tier's giftVariants DO carry
// per-product display meta (title/image/price/handle), same as Bundle's
// reward.freeGifts — reuse bundleProductsFromServer for that regrouping.
function giftScopeFromServer(scope) {
  const type = scope?.type || 'any';
  const ids = Array.isArray(scope?.ids) ? scope.ids : [];
  return {
    type,
    // Specific products reload from the scope meta the backend stores (titles,
    // images, and the exact picked variants); bare ids are only a fallback for
    // campaigns saved before that meta existed.
    auctionProducts:
      type === 'specific_product'
        ? (Array.isArray(scope?.products) && scope.products.length
            ? bundleProductsFromServer(scope.products)
            : idsToProductRows(ids))
        : [],
    auctionCollections: type === 'collection' ? idsToRuleRows('collection', ids) : [],
    auctionTags: type === 'tag' ? idsToRuleRows('tag', ids) : [],
    auctionVendors: type === 'vendor' ? idsToRuleRows('vendor', ids) : [],
    auctionProductTypes: type === 'product_type' ? idsToRuleRows('product_type', ids) : [],
  };
}

function giftTierFromServer(tier, index) {
  const giftVariants = bundleProductsFromServer(
    (tier?.giftVariants ?? []).map((g) => ({
      productId: g.productId,
      variantId: g.variantId,
      title: g.meta?.title ?? g.title ?? '',
      image: g.meta?.image ?? g.image ?? null,
      price: g.meta?.price ?? g.price,
      handle: g.meta?.handle ?? g.handle ?? '',
    }))
  );
  return {
    key: `gift-tier-loaded-${index}`,
    minSpend: tier?.minSpend ?? '',
    mode: tier?.mode === 'customer_picks' ? 'customer_picks' : 'auto_add',
    picks: tier?.picks ?? 1,
    giftVariants,
  };
}

// Transforms the server giftConfig into the shape the gift builder holds.
function giftConfigAdaptor(cfg) {
  if (!cfg) return null;
  const tiers = Array.isArray(cfg.tiers) ? cfg.tiers : [];
  return {
    title: cfg.title ?? '',
    scope: giftScopeFromServer(cfg.scope),
    oncePerOrder: cfg.oncePerOrder !== false,
    tiers: tiers.length > 0 ? tiers.map(giftTierFromServer) : [giftTierFromServer({}, 0)],
    display: cfg.display ?? 'BAR',
  };
}

function ProductAdaptorForLoad(campaign) {
  try {
    var seen = [];

    const auctionProducts = campaign.products
      .map((p) => {
        const productId = p?.productId?.toString();
        const variantId = p?.variantId?.toString();
        const isExclude = Boolean(p?.isExcluded);

        if (!productId || !variantId) return null;

        // 🔑 dedupe key (allow once in true & once in false)
        const key = `${productId}-${variantId}-${isExclude ? 1 : 0}`;
        if (seen.length > 0) {
          if (seen.filter((v) => v == key).length > 0) {
            return null;
          }
        }

        seen.push(key);
        // ✅ variant lookup with fallback
        const variants =
          campaign?.allVariantSelectedList?.filter(
            (v) => v.productId.toString() === productId && v.id.toString() === variantId
          ) || [];

        const finalVariants =
          variants.length > 0
            ? variants
            : [{ productId, id: variantId, title: '' }];
        // Deduplicate based on productId + id
        const uniqueFinalVariants = [
          ...new Map(finalVariants.map((v) => [`${v.productId}-${v.id}`, v])).values(),
        ];
        return {
          productId,
          variantId,
          isExclude,
          title: p?.meta?.title?.toString(),
          handle: p?.meta?.handle?.toString(),
          image: { src: p?.meta?.images?.[0]?.originalSrc },
          product: {
            productId,
            title: p?.meta?.title?.toString(),
            handle: p?.meta?.handle?.toString(),
            image: p?.meta?.images?.[0]?.originalSrc,
          },
          variants: uniqueFinalVariants,
          isAutomatic: p?.isAutomatic || false,
          conflictedCampaignId: p?.conflictedCampaignId ? p?.conflictedCampaignId : null,
        };
      })
      .filter(Boolean);
    return auctionProducts;
  } catch (error) {
    return [];
  }
}
function campaignProductAdaptor(campaign) {
  const data = {
    auctionCollections: campaign.collections.map((c) => ({
      collection: { title: c?.meta?.title },
      collectionId: c?.collectionId?.toString(),
      isExclude: c.isExcluded,
    })),
    // auctionProducts: campaign.products.map((p) => ({
    //   productId: p?.productId?.toString(),
    //   variantId: p?.variantId?.toString(),
    //   isExclude: p.isExcluded,
    //   title: p?.meta?.title?.toString(),
    //   handle: p?.meta?.handle?.toString(),
    //   image: {src: p?.meta?.images[0]?.originalSrc },
    //   product: {
    //     productId: p?.productId?.toString(),
    //     title: p?.meta?.title?.toString(),
    //     handle: p?.meta?.handle?.toString(),
    //     image: p?.meta?.images[0]?.originalSrc,
    //   },
    //   variants: campaign?.allVariantSelectedList?.filter(
    //     v => v.productId.toString() === p?.productId?.toString() && v.id.toString() === p?.variantId?.toString()
    //   ) || [],
    //   // variantIdList:campaign?.allVariantSelectedList?.filter(
    //   //   v => v.productId.toString() === p?.productId?.toString().map(v=>v.id.toString())
    //   // ) || []
    // })),
    auctionProducts: ProductAdaptorForLoad(campaign),
    auctionTags: campaign?.tags?.map((t) => ({
      tagName: t.tagName,
      isExclude: t.isExcluded,
    })),
    auctionVendors: campaign?.vendors?.map((t) => ({
      vendorId: t.vendorName,
      isExclude: t.isExcluded,
    })),
    auctionProductTypes: campaign?.productTypes?.map((t) => ({
      productTypeName: t.productTypeName,
      isExclude: t.isExcluded,
    })),
    auctionPriceRanges: campaign?.priceRanges?.map((t) => ({
      fromPrice: t.fromPrice,
      toPrice: t.toPrice,
      isExclude: t.isExcluded,
    })),
    auctionVariantMetafieldRules: (campaign?.variantMetafieldRules ?? []).map((r) => ({
      namespace: r.namespace,
      key: r.key,
      name: r.name,
      type: r.type,
      operator: r.operator,
      value: r.value,
    })),
    variantMetafieldMatchType: campaign?.variantMetafieldMatchType ?? 'all',
  };

  return data;
}

export function singleCampaignFormAdaptor(campaign) {
  const base = baseSingleCampaignFormAdaptor(campaign);
  const productData = campaignProductAdaptor(campaign);

  // Transform flat bogoConfig from server into nested structure with full product objects
  if (base.bogoConfig) {
    base.bogoConfig = bogoConfigAdaptor(base.bogoConfig);
  }
  // Same for the Product Bundle set config (slots regrouped like buyProducts).
  if (base.bundleConfig) {
    base.bundleConfig = bundleConfigAdaptor(base.bundleConfig, campaign);
  }
  // Same for the Cross-sell trigger/offer/reward config.
  if (base.xsellConfig) {
    base.xsellConfig = xsellConfigAdaptor(base.xsellConfig);
  }
  // Same for the Free Gift scope/tiers/display config.
  if (base.giftConfig) {
    base.giftConfig = giftConfigAdaptor(base.giftConfig);
  }
  // Same for the Upsell trigger/ladder/offer/incentive config.
  if (base.upsellConfig) {
    base.upsellConfig = upsellConfigAdaptor(base.upsellConfig);
  }

  const data = { ...base, ...productData };
  return { data, success: true, message: 'ok' };
}
